import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { FilterEmptyPipe } from '@zaoblako/web/shared/pipes/filter-empty.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { ButtonModule } from 'primeng/button';
import { DomHandler } from 'primeng/dom';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule, TableService } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';
import { Key } from 'ts-key-enum';

// НЕ ДОБАВЛЯТЬ changeDetection, убьет!
@Component({
  standalone: true,
  selector: 'crm-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [
    CommonModule,
    TableModule,
    OrderByPipe,
    MultiSelectComponent,
    ButtonModule,
    SkeletonModule,
    ProgressSpinnerModule,
    NoDataComponent,
    FilterEmptyPipe
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    DomHandler,
    ObjectUtils,
    TableService,
    {
      provide: Table,
      useFactory: (crmTable: TableComponent) => crmTable.table,
      deps: [TableComponent]
    }
  ]
})
export class TableComponent implements OnInit, OnChanges {
  @ViewChild('table', { static: true }) private table: Table;

  @ContentChild('headerTemplate') public headerTemplate;
  @ContentChild('groupheaderTemplate') public groupheaderTemplate;
  @ContentChild('rowTemplate') public rowTemplate;
  @ContentChild('footerTemplate') public footerTemplate;
  @ContentChild('captionTemplate') public captionTemplate;

  @Input() public loading = false;
  @Input() public paginationLoading = false;

  @Input() public selectable = false;
  @Input() public selectionDisabled = false;
  @Input() public selectionMode: string;
  @Input() public dataKey = 'id';
  @Input() public selectedItems: any[] = [];
  @Output() public selectedItemsChange = new EventEmitter<any[]>();
  @Output() public filterColumnsChange = new EventEmitter<string[]>();

  @Input() public isVisualSelectionRows = true;
  @Input() public visualSelectedRows: string[] = [];
  private indexVisualSelectedRows = 0;

  @Input() public name: string;
  @Input() public values: any[] = [{}];
  @Input() public columns: CrmTableColumn[] = [];
  public selectedColumns: CrmTableColumn[] = [];
  public selectedColumnsMap: Record<string, boolean>;

  @Input() public filterColumns = false;

  @Input() public sort = '';
  @Output() public sortChange = new EventEmitter<string>();

  @Input() public inplaceSort = true;
  @Input() public styleClass = '';
  @Input() public scrollHeight: string;
  @Input() public scrollable = false;
  @Input() public responsiveLayout: string;

  @Input() public groupRowsBy: string;
  @Input() public rowGroupMode: string;

  @Input() public autoLayout = false;

  @Input() public skeletonItemsCount = 15;
  public skeletonItems = [];

  @Input() public selectedColumnNames: string[];

  public constructor(private readonly renderer: Renderer2) {}

  public ngOnInit(): void {
    if (!this.sort) {
      this.sort =
        localStorage.getItem(`table.${this.name}.sort`) ??
        this.columns.find((column) => column.sort && column.defaultSort)?.sort ??
        this.columns.find((column) => column.sort)?.sort ??
        '';
    }

    this.skeletonItems = Array(this.skeletonItemsCount).fill({});

    this.setSelectedColumns();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.columns?.currentValue) {
      this.columns.forEach((element, index) => {
        if (!element.order) {
          element.order = index;
        }
      });
    }

    if (changes.columns?.currentValue || changes.selectedColumnNames?.currentValue) {
      this.setSelectedColumns();
    }

    if (changes.values?.currentValue) {
      setTimeout(() => {
        if (this.isVisualSelectionRows) {
          this.handlerSelectedRow();
        }
      }, 2000);
    }
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (this.isVisualSelectionRows && this.visualSelectedRows?.length === 1) {
      const rowsBody = (this.table?.tableViewChild?.nativeElement as HTMLTableElement)?.tBodies?.[0]?.rows;
      if (rowsBody && event.code === Key.ArrowUp) {
        const rowIndex = Array.from(rowsBody).findIndex(
          (row) => row.getAttribute('id_row') === this.visualSelectedRows[0]
        );
        if (!rowIndex || rowIndex === -1) {
          return;
        }
        this.renderer.removeClass(rowsBody[rowIndex], 'visualSelectedRow');
        this.renderer.addClass(rowsBody[rowIndex - 1], 'visualSelectedRow');
        this.visualSelectedRows[0] = rowsBody[rowIndex - 1].getAttribute('id_row');
        event.preventDefault();

        const rect = rowsBody[rowIndex - 1].getBoundingClientRect();
        const topShown = rect.top >= 0;
        if (!topShown) {
          window.scrollTo({ top: window.scrollY + rect.top, behavior: 'smooth' });
        }
      } else if (rowsBody && event.code === Key.ArrowDown) {
        const rowIndex = Array.from(rowsBody).findIndex(
          (row) => row.getAttribute('id_row') === this.visualSelectedRows[0]
        );
        if (rowIndex + 1 === rowsBody.length || rowIndex === -1) {
          return;
        }
        this.renderer.removeClass(rowsBody[rowIndex], 'visualSelectedRow');
        this.renderer.addClass(rowsBody[rowIndex + 1], 'visualSelectedRow');
        this.visualSelectedRows[0] = rowsBody[rowIndex + 1].getAttribute('id_row');
        event.preventDefault();

        const rect = rowsBody[rowIndex + 1].getBoundingClientRect();
        const bottomShown = rect.bottom <= window.innerHeight - 60;
        if (!bottomShown) {
          window.scrollTo({ top: rect.bottom + window.scrollY - (window.innerHeight - 60), behavior: 'smooth' });
        }
      }
    }
  }

  public setSelectedColumns() {
    if (this.name?.length) {
      const selectedColumnsNames = localStorage.getItem(`table.${this.name}.columns`);
      this.selectedColumns = selectedColumnsNames
        ? this.columns.filter((col) => selectedColumnsNames.includes(col.name))
        : [...this.columns];
    }
    this.selectedColumns = this.selectedColumnNames
      ? this.columns.filter((col) => this.selectedColumnNames.includes(col.name) || !col.name || col.required)
      : [...this.columns];
    this.updateSelectedColumnsMap();
  }

  public getSortIconClass(sort: string): string {
    const type = sort.split(':')?.[1];
    if (type === 'number') {
      return this.sort.includes(sort)
        ? this.sort[0] === '-'
          ? 'pi pi-sort-numeric-up'
          : 'pi pi-sort-numeric-down'
        : 'pi pi-sort-alt';
    } else if (['date', 'boolean'].includes(type)) {
      return this.sort.includes(sort) ? (this.sort[0] === '-' ? 'pi pi-sort-up' : 'pi pi-sort-down') : 'pi pi-sort';
    } else {
      return this.sort.includes(sort)
        ? this.sort[0] === '-'
          ? 'pi pi-sort-alpha-up'
          : 'pi pi-sort-alpha-down'
        : 'pi pi-sort-alt';
    }
  }

  public setSort(sort: string): void {
    this.sort = this.sort === sort ? (sort[0] === '-' ? sort.substring(1) : `-${sort}`) : sort;
    if (this.name?.length) {
      localStorage.setItem(`table.${this.name}.sort`, this.sort);
    }
    if (this.groupRowsBy) {
      this.values = [...this.values];
    }

    this.sortChange.emit(this.sort);
  }

  public updateSelectedColumnsMap() {
    this.selectedColumnsMap = {};
    this.selectedColumns.forEach((col) => (this.selectedColumnsMap[col.name] = true));
  }

  public setFilterColumns(selectedColumns: CrmTableColumn[]) {
    this.updateSelectedColumnsMap();
    if (this.name?.length) {
      localStorage.setItem(`table.${this.name}.columns`, JSON.stringify(selectedColumns.map((col) => col.name)));
    }
    this.filterColumnsChange.emit(selectedColumns.map((col) => col.name));
  }

  public toColumn(column: any): CrmTableColumn {
    return column as CrmTableColumn;
  }

  private handlerSelectedRow() {
    const rowsBody = (this.table?.tableViewChild?.nativeElement as HTMLTableElement)?.tBodies?.[0]?.rows;
    if (rowsBody?.length) {
      const clearAll = () => {
        if (this.visualSelectedRows?.length) {
          const selectedRows = Array.from(rowsBody).filter((el) =>
            this.visualSelectedRows.includes(el.getAttribute('id_row'))
          );
          for (const row of selectedRows) {
            if (this.visualSelectedRows.includes(row.getAttribute('id_row'))) {
              this.renderer.removeClass(row, 'visualSelectedRow');
            }
          }
          this.visualSelectedRows = [];
        }
      };

      const searchToggleIcon = (element: Element) => {
        if (element.classList?.contains('table-toggler') && element.getAttribute('listener') !== 'true') {
          this.renderer.listen(element, 'click', () => {
            this.handlerSelectedRow();
          });
          element.setAttribute('listener', 'true');
        }

        const children = element.children;
        for (let i = 0; i < children.length; i++) {
          searchToggleIcon(children[i]);
        }
      };

      const toggleRow = (id_row: string, isCtrlKey: boolean) => {
        const elementRow: HTMLTableRowElement = Array.from(rowsBody).find((el) => el.getAttribute('id_row') === id_row);
        if (this.visualSelectedRows.includes(id_row)) {
          const isClickNotCtrlInMultiple = this.visualSelectedRows?.length > 1 && !isCtrlKey;
          if (!isCtrlKey) {
            clearAll();
          }
          if (!isClickNotCtrlInMultiple) {
            this.visualSelectedRows = this.visualSelectedRows.filter((id) => id !== id_row);
            this.renderer.removeClass(elementRow, 'visualSelectedRow');
          } else {
            this.visualSelectedRows.push(id_row);
            this.renderer.addClass(elementRow, 'visualSelectedRow');
          }
        } else {
          if (!isCtrlKey) {
            clearAll();
          }
          this.visualSelectedRows.push(id_row);
          this.renderer.addClass(elementRow, 'visualSelectedRow');
        }
      };

      for (let i = 0; i !== rowsBody.length; i++) {
        searchToggleIcon(rowsBody[i]);

        if (!rowsBody[i].getAttribute('id_row')) {
          const id_row = `${this.name}_${this.indexVisualSelectedRows++}`;
          rowsBody[i].setAttribute('id_row', id_row);
          this.renderer.listen(rowsBody[i], 'click', (event: MouseEvent) => {
            toggleRow(id_row, event.ctrlKey || event.metaKey);
          });
        }
      }
    }
  }
}
